import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import TiptapEditor from '../../tiptapEditor';
import { CommentFilesInterface } from '../../../interfaces/TaskInterface';
import { Emojis, FileBox, IconLeftSection, IconSection, MessageInputSection, TextBox, UploadDiv } from './styles';
import CommentDropdown from '../../dropdowns/commentDropdown';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { UserInterface } from '../../../interfaces/chatMessageInterface';
import { ReplyMessageDataInterface } from '../../../interfaces/MessageInterface';
import { captureException } from '../../../services/logService';
import {
  DEFAULT_UPLOAD_FOLDER,
  handleFileUpload,
  isMAC,
  isMediaRecordingSupported,
  removeFileFromFirebase
} from '../../../helpers/common';
import RecordingSnapAttach from '../../recording';
import { RenderFilePreview } from '../../renderFilePreview';
import { Dropdown } from '../../dropdown';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { dispatchChatMessageError } from '../../../services/chatMessageUtilService';
import { rootStore } from '../../../mobx/rootStore';
import { TiptapEditorRef } from '../../../interfaces/tiptapInterface';
import { observer } from 'mobx-react-lite';
import Tooltip from '../../tooltip';
import { useMobileDevice } from '../../../global/useMobile';
import IconButton from '../../button/iconButton';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../../global/constants';
import BottomNavigationItem from '../../bottomNavigation/subNavigation/bottomNavigationItem';
import { getMentionUsersWithEveryone } from '../../../helpers/mentionHelper';

interface Props {
  onClickSelectFile: () => void;
  isEveryoneMentionEnable?: boolean;
  users: UserInterface[];
  sendComment: (files: CommentFilesInterface[]) => void;
  replyMessage: ReplyMessageDataInterface;
  setReplyMessage: (value: ReplyMessageDataInterface) => void;
  isSelectFileEnable: boolean;
  isProjectDetail?: boolean;
  handleOpenSnapModal: () => void;
  handleOpenGifModal: () => void;
  isCalendarViewCard?: boolean;
  isAutoFocus?: boolean;
  disable: boolean;
  isKeyboardVisible?: boolean;
  iconSectionRef?: React.RefObject<HTMLDivElement>;
  uploadFolder?: string;
}

const MessagesInputBox = forwardRef((props: Props, ref) => {
  const {
    onClickSelectFile,
    isEveryoneMentionEnable,
    users,
    sendComment,
    replyMessage,
    setReplyMessage,
    isSelectFileEnable,
    isProjectDetail,
    handleOpenSnapModal,
    handleOpenGifModal,
    isAutoFocus = true,
    disable,
    isKeyboardVisible,
    // setIsKeyboardVisible,
    iconSectionRef,
    uploadFolder = DEFAULT_UPLOAD_FOLDER
  } = props || {};
  // Refs
  const editorRef = useRef<TiptapEditorRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiDropdownRef = useRef<{ closeDropdown: () => void }>(null);
  // States
  const [isDragging, setIsDragging] = useState(false);
  const setDragCounter = useState(0)[1];
  const [simpleInputValue, setSimpleInputValue] = useState('');

  // Mobx store variables
  const { projectStore, currentUser, richTextStore } = rootStore;
  const { uploadFilePercent } = projectStore;
  const {
    content,
    setContent,
    uploadedFiles,
    setUploadedFiles,
    addUploadedFiles,
    removeUploadedFile,
    isLoading,
    setLoading,
    reset
  } = richTextStore;

  // Other variables
  const isMobile = useMobileDevice();

  // Function to extract first line from HTML content
  const getFirstLineFromContent = (htmlContent: string): string => {
    if (!htmlContent) return '';

    // Create a temporary element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // Get text content and split by newlines
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    const lines = textContent?.split('\n');

    // Return first line, trimmed
    return lines[0]?.trim() || '';
  };

  // Update simple input when content changes
  useEffect(() => {
    if (isMobile && !isKeyboardVisible) {
      const firstLine = getFirstLineFromContent(content);
      setSimpleInputValue(firstLine);
    }
  }, [content, isMobile, isKeyboardVisible]);

  const usersWithAll = getMentionUsersWithEveryone(users, currentUser?.id);

  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only show DropBox for external file drags
      const hasFiles = e.dataTransfer?.types.includes('Files');
      if (!hasFiles) return;

      setDragCounter((prev) => {
        const newVal = prev + 1;
        if (newVal > 0) setIsDragging(true);
        return newVal;
      });
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only handle file drags
      const hasFiles = e.dataTransfer?.types.includes('Files');
      if (!hasFiles) return;

      setDragCounter((prev) => {
        const newVal = prev - 1;
        if (newVal <= 0) {
          setIsDragging(false);
          return 0;
        }
        return newVal;
      });
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Only handle file drags
      const hasFiles = e.dataTransfer?.types.includes('Files');
      if (!hasFiles) return;

      setIsDragging(false);
      setDragCounter(0);
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    window.addEventListener('dragend', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
      window.removeEventListener('dragend', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
    };
  }, [setDragCounter]);

  useImperativeHandle(ref, () => ({
    addRecordingCard(fileUrl: string, fileName: string) {
      editorRef.current?.addRecordingCard(fileUrl, fileName);
    },
    messageInputFocus() {
      editorRef?.current?.messageInputFocus();
    },
    addImageInContent(fileUrl: string) {
      editorRef.current?.addImage(fileUrl);
    },
    addContent(value: string) {
      editorRef.current?.addContent(value);
    },
    clearContent() {
      editorRef?.current?.clearValue();
      reset();
    }
  }));

  const handleFileInput = useCallback(() => {
    queueMicrotask(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    });
  }, []);

  const handleEmojiSelected = (emojiData: EmojiClickData) => {
    const currentEmoji = UserPreferenceSingleton.getInstance().getUserUseEmoji();
    const updatedEmojis = [
      emojiData?.emoji,
      ...(Array.isArray(currentEmoji) ? currentEmoji.filter((e) => e !== emojiData?.emoji) : [])
    ]?.slice(0, 2);
    UserPreferenceSingleton.getInstance().setUserUseEmoji(updatedEmojis);
    editorRef.current?.addContentExistingLine(emojiData.emoji);
    // On mobile, close the picker after selecting an emoji
    if (isMobile) {
      emojiDropdownRef.current?.closeDropdown();
    }
  };

  const onSendComment = useCallback(() => {
    if (sendComment) {
      sendComment(uploadedFiles);
      // Blur the input field on mobile after sending to remove focus
      if (isMobile) {
        // Try multiple approaches to ensure blur works
        if (editorRef?.current) {
          editorRef.current.messageInputBlur();
          // Also try direct DOM blur as fallback
          const editorElement = document.querySelector('.ProseMirror');
          if (editorElement) {
            (editorElement as HTMLElement).blur();
          }
          // Also try blurring the active element
          if (document.activeElement) {
            (document.activeElement as HTMLElement).blur();
          }
        }
      }
    }
  }, [sendComment, uploadedFiles, isMobile]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault(); // Prevent default behavior if needed
      if (isLoading) return;
      onSendComment();
    }
  };

  const addDroppedFiles = useCallback(
    (files: CommentFilesInterface[]) => {
      addUploadedFiles(files);
    },
    [addUploadedFiles]
  );

  const onFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files || files.length === 0) {
        dispatchChatMessageError('No files selected.');
        return;
      }
      try {
        setLoading(true);
        const fileArray = Array.from(files); // Convert FileList to Array

        //Call common function to upload file in firebase
        const updatedFiles = await handleFileUpload(uploadedFiles || [], fileArray, editorRef, undefined, uploadFolder);
        const value = [...(uploadedFiles || []), ...(updatedFiles || [])];
        setUploadedFiles(value);
      } catch (error) {
        captureException(error);
        dispatchChatMessageError('Error occurred while uploading files.');
      } finally {
        setLoading(false);
      }
    },
    [setLoading, uploadedFiles, setUploadedFiles, uploadFolder]
  );

  const onClickClose = useCallback(
    (index: number, file: CommentFilesInterface) => {
      if (file && !file.uploadFileType && file.href.startsWith('https://firebasestorage.googleapis'))
        removeFileFromFirebase(file.href);
      removeUploadedFile(index);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Refocus the editor after removing the file
      queueMicrotask(() => {
        editorRef.current?.messageInputFocus();
      });
    },
    [removeUploadedFile]
  );

  const uploadSnapEmbed = useCallback((firebaseUrl: string, fileName: string, videoId: string) => {
    editorRef?.current?.addRecordingCard(firebaseUrl, fileName, videoId);
  }, []);

  // Renders the send button
  const renderSendButton = useCallback(() => {
    return isMobile ? (
      <BottomNavigationItem iconName='send-icon' onClick={onSendComment} />
    ) : (
      <IconButton
        iconName='send-icon'
        size={ICON_BUTTON_SIZE.LARGE}
        variant={ICON_BUTTON_TYPE.TERTIARY}
        onClick={onSendComment}
        isDisabled={isLoading}
      />
    );
  }, [isLoading, isMobile, onSendComment]);

  return (
    <>
      <MessageInputSection
        isKeyboardVisible={isKeyboardVisible}
        isProjectDetail={isProjectDetail}
        isBubbleMenuOpen={rootStore.appStore.isBubbleMenuOpen}>
        <TextBox
          onKeyDown={handleKeyDown}
          isKeyboardVisible={isKeyboardVisible}
          isApplyManualHeight={!isMobile && isDragging}
          simpleContent={!isKeyboardVisible}>
          {isMobile && !isKeyboardVisible && (
            <Dropdown
              modalTitle='Attach File'
              activeClassName='active'
              content={<BottomNavigationItem iconName='plus-icon' />}>
              <CommentDropdown
                onClickUploadFile={handleFileInput}
                onClickSelectFile={onClickSelectFile}
                isSelectFileEnable={isSelectFileEnable}
              />
            </Dropdown>
          )}
          <TiptapEditor
            disabled={disable}
            ref={editorRef}
            valueContent={disable ? '' : isMobile && !isKeyboardVisible ? simpleInputValue : content}
            setValueContent={setContent}
            placeholder='Type a message'
            users={isEveryoneMentionEnable ? usersWithAll : users?.filter((user) => user?.id !== currentUser?.id)}
            isAutoFocus={isMobile ? false : isAutoFocus}
            isMessageInputBox={true}
            isKeyboardVisible={isKeyboardVisible}
            isMobile={isMobile}
            upload={{
              setLoading: (value: boolean) => setLoading(value),
              setStateLoading: (value: boolean) => setLoading(value),
              addUploadedFiles: addDroppedFiles,
              uploadFolder
            }}
            reply={{
              replyMessage: disable ? undefined : replyMessage,
              setReplyMessage
            }}
            toolbarActions={{
              recordSnapModalOpen: handleOpenSnapModal,
              handleFileInput,
              handleGifModal: handleOpenGifModal
            }}
            dropBox={{ isVisible: true }}
          />

          {isMediaRecordingSupported() && (
            <RecordingSnapAttach setLoading={(value: boolean) => setLoading(value)} uploadSnapEmbed={uploadSnapEmbed} />
          )}
          {isLoading && (!isMobile || (isMobile && isKeyboardVisible)) && (
            <UploadDiv>Uploading ({Math.floor(uploadFilePercent)}%)</UploadDiv>
          )}
          <input type='file' ref={fileInputRef} hidden onChange={onFileUpload} multiple aria-label='Upload file' />

          {uploadedFiles?.length > 0 && !disable && (!isMobile || (isMobile && isKeyboardVisible)) && (
            <FileBox isApplyMargin={uploadedFiles?.length > 0 ? 'var(--space-32)' : 'var(--space-0)'}>
              {uploadedFiles?.length > 0 &&
                uploadedFiles?.map((file: CommentFilesInterface, index: number) => {
                  return (
                    <>
                      <RenderFilePreview
                        key={file?.href}
                        file={file}
                        maxWidth={240}
                        isClose={true}
                        onClose={() => {
                          onClickClose(index, file);
                        }}
                      />
                    </>
                  );
                })}
            </FileBox>
          )}
        </TextBox>
        {(!isMobile || (isMobile && isKeyboardVisible)) && (
          <IconSection tabIndex={-1} ref={iconSectionRef}>
            <IconLeftSection>
              <Dropdown
                ref={emojiDropdownRef}
                isRemoveTopPadding
                isMinWidth={258}
                isRemoveMaxHeight
                position='right'
                onOutsideClick={() => {
                  // Focus back to the editor when dropdown closes
                  editorRef.current?.messageInputFocus();
                }}
                content={
                  isMobile ? (
                    <BottomNavigationItem iconName='updated-emoji-icon' />
                  ) : (
                    <IconButton
                      iconName='updated-emoji-icon'
                      size={ICON_BUTTON_SIZE.LARGE}
                      variant={ICON_BUTTON_TYPE.TERTIARY}
                    />
                  )
                }>
                <Emojis onClick={(e) => e.stopPropagation()}>
                  <EmojiPicker
                    searchPlaceholder='Search Emoji'
                    theme={Theme.AUTO}
                    width={!isMobile ? 258 : undefined}
                    height={240}
                    onEmojiClick={handleEmojiSelected}
                    lazyLoadEmojis={true}
                  />
                </Emojis>
              </Dropdown>

              <Dropdown
                modalTitle='Attach File'
                activeClassName='active'
                closeOnItemClick={!isMobile}
                onOutsideClick={() => {
                  editorRef.current?.messageInputFocus();
                }}
                content={
                  isMobile ? (
                    <BottomNavigationItem iconName='file-attachment-icon' />
                  ) : (
                    <IconButton
                      iconName='file-attachment-icon'
                      size={ICON_BUTTON_SIZE.LARGE}
                      variant={ICON_BUTTON_TYPE.TERTIARY}
                    />
                  )
                }>
                <CommentDropdown
                  onClickUploadFile={handleFileInput}
                  onClickSelectFile={onClickSelectFile}
                  isSelectFileEnable={isSelectFileEnable}
                />
              </Dropdown>
            </IconLeftSection>

            {/* Render send button with keyboard shortcut tooltip (cmd + return to send or ctrl + enter to send) on desktop; skip tooltip on mobile for cleaner UI. */}
            {!isMobile ? (
              <Tooltip
                maxWidth={'max-content'}
                title={isMAC() ? 'cmd + return to send message' : 'ctrl + enter to send message'}>
                {renderSendButton()}
              </Tooltip>
            ) : (
              renderSendButton()
            )}
          </IconSection>
        )}
      </MessageInputSection>
    </>
  );
});

export default observer(MessagesInputBox);
